import { useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";
import { trpc } from "lib/trpc";
import { ClientNotification } from "@compass/shared/types";

type Navigate = ReturnType<typeof useNavigate>;
type QueryClient = ReturnType<typeof trpc.useContext>;
type MarkAsReadMutation = {
    mutate: (input: { id: number }, opts?: { onSuccess?: () => void }) => void;
};

interface Args {
    notification: ClientNotification;
    navigate: Navigate;
    queryClient: QueryClient;
    markAsReadMutation: MarkAsReadMutation;
}

/**
 * Pure dispatch — marks the notification as read and navigates to the matching
 * route by `data.type`. Exported for tests and for callers that already own
 * the deps; UI consumers should reach for `useNotificationClick` instead so
 * they don't have to wire the same three hooks every time.
 */
export function handleNotificationClick({
    notification,
    navigate,
    queryClient,
    markAsReadMutation,
}: Args) {
    const data = notification.data;

    markAsReadMutation.mutate(
        { id: notification.id },
        {
            onSuccess: async () => {
                // More aggressive cache invalidation to prevent race conditions
                await queryClient.notifications.latest.invalidate();
                await queryClient.notifications.latest.refetch();
            },
        },
    );

    // Covers every `ReplyNotificationSchema` variant in
    // `packages/shared/schema/notification.ts`. Falling back to `break` here
    // leaves the user stranded on a mark-as-read with no navigation, which is
    // the bug PRTL-1009 was filed for.
    switch (data.type) {
        case "friend_request":
            navigate({ to: "/friends" });
            break;
        case "social_comment":
            if (data.postId != null) {
                navigate({
                    to: "/social/post/$id",
                    params: { id: String(data.postId) },
                });
            }
            break;
        case "message":
            if (data.conversationId) {
                navigate({
                    to: "/messages/$id",
                    params: { id: data.conversationId },
                });
            } else if (data.postId != null) {
                navigate({
                    to: "/messages/$id",
                    params: { id: String(data.postId) },
                });
            } else {
                navigate({ to: "/messages" });
            }
            break;
        case "discussion": {
            const discussionId =
                data.discussionId ?? (data.postId != null ? Number(data.postId) : null);
            if (discussionId != null) {
                navigate({ to: "/discussions", search: { id: discussionId } });
            } else {
                navigate({ to: "/discussions" });
            }
            break;
        }
        case "ai_digest":
            navigate({ to: "/digest/$id", params: { id: String(data.digestId) } });
            break;
        case "feed_digest":
            navigate({ to: "/rss-feeds" });
            break;
        case "article":
            navigate({ to: "/p/$slug", params: { slug: data.articleSlug } });
            break;
        case "blog_posts_digest":
            // Land on the first preview post — the digest itself doesn't
            // have a dedicated landing page in the SPA. Schema enforces
            // `topPosts.min(1)`, so the array is always populated; the
            // length-check is a runtime safety net for legacy rows that
            // somehow predated the constraint.
            if (data.topPosts.length > 0) {
                navigate({ to: "/p/$slug", params: { slug: data.topPosts[0].slug } });
            } else {
                navigate({ to: "/" });
            }
            break;
        case "client_file_report_comment":
        case "client_file_report_published":
            navigate({ to: data.reportType === "budget" ? "/budget" : "/benchmark" });
            break;
        case "visualization_comment":
        case "visualization_published":
            navigate({
                to: "/visualizations/$id",
                params: { id: String(data.chartId) },
            });
            break;
        case "document_upload":
            // Admin upload alert (PRTL-1322) — land on the documents library.
            navigate({
                to: "/documents",
                search: { showPublicFiles: true, showPrivateFiles: true },
            });
            break;
        default: {
            // Compile-time exhaustiveness guard.
            const _exhaustive: never = data;
            void _exhaustive;
            break;
        }
    }
}

/**
 * Hook that returns a single `onClick(notification)` callback for a
 * notification row. Consumers (the bell dropdown, the full-screen modal, and
 * any future surface that renders notifications) call this once at the top
 * of the component instead of importing the helper, instantiating the
 * mark-as-read mutation, and grabbing `useNavigate` + `trpc.useContext`
 * separately each time.
 */
export function useNotificationClick() {
    const navigate = useNavigate();
    const queryClient = trpc.useContext();
    const markAsReadMutation = trpc.notifications.markAsRead.useMutation();

    return useCallback(
        (notification: ClientNotification) => {
            handleNotificationClick({
                notification,
                navigate,
                queryClient,
                markAsReadMutation,
            });
        },
        [navigate, queryClient, markAsReadMutation],
    );
}
